import {
  Component,
  ElementRef,
  Input,
  OnInit,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  Output,
  EventEmitter,
} from "@angular/core";
import {
  phaseAbbreviations,
  GetGridApiParams,
  GetTotalLineApiParams,
  GridColumnNames,
  GridRowObj,
  NoDataMsg,
  BreakOutColsAllowed,
} from "../pharma-page.model";
import {
  expandArrows,
  collapseArrows,
  caretRight,
  caretDown,
  VAIconRegistryService,
  infoCircle,
} from "@va/core-library/icons";

import { formatCell } from "src/app/utils/common";
import { PharmaDataService } from "../pharma-data.service";
import { MatSort, Sort, SortDirection } from "@angular/material/sort";

import { Subscription } from "rxjs";
import { SelectionModel } from "@angular/cdk/collections";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { Store } from "@ngrx/store";
import {
  SetDpParams,
  DeleteDpParams,
} from "src/app/store/actions/global-subheader.actions";
import {
  REL_BASE_YEAR,
  FINANCIAL_BASIS_LABEL,
  CALENDAR_BASIS_LABEL,
  CURRENT_YEAR,
} from "@va/core-library/period-filter";
import { environment } from "../../../environments/environment";
import { NzMessageService } from "ng-zorro-antd/message";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { MatMenuTrigger } from "@angular/material/menu";

@Component({
  selector: "app-database-grid",
  templateUrl: "./database-grid.component.html",
  styleUrls: ["./database-grid.component.scss"],
})
export class DatabaseGridComponent implements OnInit, OnDestroy {
  @Input() gridSettings;
  @Input() indicationBreakout;
  @Input() showNIB;
  @ViewChild(MatTable) table: MatTable<any>;
  @Output() fullExportFlagChanged: EventEmitter<any> = new EventEmitter();
  @Output() totalCountChanged: EventEmitter<any> = new EventEmitter();
  @Output() consolidatedCountChanged: EventEmitter<any> = new EventEmitter();
  @Output() showNIBChanged: EventEmitter<any> = new EventEmitter();
  @ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;
  contextMenuPosition = { x: "0px", y: "0px" };

  assets = environment.assetUrl;
  faSpinner = faSpinner;
  pageNumber = 0;
  isGridReady = false;
  staticDisplayColumns = ["drug_name", "ticker"];
  periodList = [];
  selectedRows = [];
  displayColumns = [];
  columnList = [];
  drugsGridData: {
    rows: Array<any>;
    rowData: any;
    columns: Array<any>;
  };
  dataSource: MatTableDataSource<GridRowObj>;
  expandedElement: GridRowObj | null;
  phaseAbbr;
  precision = 2;
  errorMsg = "";
  showError = false;
  sortKey: string;
  sortOrder: SortDirection;
  offset: number;
  limit = 20;
  totalRows = 100;
  consolidatedTotal = 0;
  download_limit = 100;
  full_export_access: boolean;
  magnitudeMsg = "";
  gridColNames = GridColumnNames;
  viewBy = "";
  forceRerender = false;
  isTotalLoading = false;

  contextMenuReady: boolean = false;
  nonPeriodPayload: {
    companyid: any;
    dapparameterid: any;
    revenue_type: any;
    ctagid: number;
    col?: "pos" | "peak_sales"; // Make col optional
    period_basis: any;
    is_breakout: boolean;
    is_geo: boolean;
  };
  auditTrailResponse: any;
  totalLineData: any = {};

  @ViewChild(MatSort) sortObj: MatSort;

  @ViewChild("divScroll") divScroll: ElementRef;
  dataCallSubscription: Subscription;
  sortAllowedOn = [
    "drug_name",
    "ticker",
    "revenue_type",
    "drug_type",
    "approval_date",
    "expiry_date",
    "companyname",
    "peak_value",
    "aggregate_year_5",
    "launch_year",
    "probability_of_success",
    "therapeutic_area",
    "peak_year ",
  ];
  selection: SelectionModel<any>;
  selectedCell: string;
  selectedDrug: string;
  scrollDisabled = false;
  excelExportSubscriber: Subscription;
  breakoutSubscriber: Subscription;
  dpCallSubscriber: Subscription;
  totalLineCallSubscriber: Subscription;
  showDrugModal = false;
  hasDrugPageAccess = false;
  accessSubscriber: Subscription;
  sourceAvailable: boolean;

  constructor(
    private iconRegistry: VAIconRegistryService,
    private service: PharmaDataService,
    private store: Store<AppState>,
    private message: NzMessageService,
  ) {
    this.iconRegistry.registerIcons([
      expandArrows,
      collapseArrows,
      caretDown,
      caretRight,
      infoCircle,
    ]);
    this.isGridReady = false;
    this.phaseAbbr = phaseAbbreviations;
    this.drugsGridData = {
      rows: [],
      rowData: {},
      columns: [],
    };
    this.sortKey = "";
    this.sortOrder = "desc";
    this.offset = 0;
    const initialSelection = [];
    const allowMultiSelect = true;
    this.selection = new SelectionModel<any>(
      allowMultiSelect,
      initialSelection,
    );
  }

  ngOnInit() {
    this.offset = 0;
    this.showError = false;
    this.dataSource = new MatTableDataSource([]);
    this.excelExportSubscriber = this.service.onExcelExport.subscribe(() => {
      if (this.totalRows > this.download_limit) {
        this.message.info(
          `Only ${this.download_limit} Drugs could be exported.`,
        );
      }
      this.service.logUserAction("EXCEL_EXPORT", {
        total_drugs: this.totalRows,
      });
    });
    this.accessSubscriber = this.store
      .select(stateGetter("globalHeader", "globalSettings", "pharmaAccess"))
      .subscribe((data: any) => {
        if (data && data.hasBiopharmaDrugPageAccess)
          this.hasDrugPageAccess = data.hasBiopharmaDrugPageAccess;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.gridSettings &&
        changes.gridSettings.currentValue &&
        changes.gridSettings.currentValue.options.filter) ||
      changes.showNIB
    ) {
      this.showError = false;
      this.isGridReady = false;
      this.getGridDataCall(true);
    }
    if (changes.indicationBreakout) {
      const currentBreakoutValue = changes.indicationBreakout.currentValue;
      const prevBreakoutValue = changes.indicationBreakout.previousValue;
      if (
        prevBreakoutValue !== void 0 &&
        currentBreakoutValue !== prevBreakoutValue
      ) {
        if (currentBreakoutValue) {
          this.expandAllBreakouts();
        } else {
          this.collapseAllBreakouts();
        }
      }
    }
    if (changes.showNIB) {
      this.showNIB = changes.showNIB.currentValue;
      this.gridSettings.showNIB = this.showNIB;
    }
  }

  getDefaultSort() {
    const isCalendar =
      this.gridSettings.options.data.periodData.period_basis === "calendar";
    const isAbsType =
      this.gridSettings.options.data.periodData.period_type === "absolute";
    let currentYear = "";

    if (isAbsType) {
      currentYear = isCalendar
        ? `${CALENDAR_BASIS_LABEL}-${CURRENT_YEAR}`
        : `${FINANCIAL_BASIS_LABEL}-${REL_BASE_YEAR}`;
    } else {
      currentYear = isCalendar ? "C1" : "F1";
    }
    if (
      (this.sortKey !== "" && !this.displayColumns.includes(this.sortKey)) ||
      this.sortKey === ""
    ) {
      const oldSortKey = this.sortKey;
      if (this.displayColumns.includes(currentYear)) {
        this.sortKey = currentYear;
      } else {
        this.sortKey = this.periodList[0];
      }
      //Handling for material table issue of not showing sort icon after changing data/revision
      //from 'revision' to 'values'
      if (oldSortKey === "" && this.sortObj && !this.forceRerender) {
        this.forceRerender = true;
        setTimeout(() => {
          this.forceRerender = false;
        }, 0);
      }
    }
    this.viewBy =
      this.gridSettings.options.data.viewBy === "values"
        ? "Analytics"
        : this.gridSettings.options.data.viewBy === "percentage_total"
          ? "% of Total"
          : "YoY%";
    if (
      this.gridSettings.options.data.viewBy === "revision" &&
      this.gridSettings.options.data.periodData.period.indexOf(this.sortKey) >
        -1
    ) {
      this.sortKey = "";
    }
  }

  openDataRevisionPopup() {
    this.service.dataRevisionPopupVisiblity.next(true);
  }
  sortData(sort: Sort) {
    this.sortKey = sort.active;
    this.sortOrder = sort.direction;
    this.getGridDataCall(true);
  }
  onRowClick(row) {
    this.selectedRows.push(row);
  }
  loadMoreItems() {
    //add more items
    this.getGridDataCall(false);
  }
  updateParams(isFirstCall) {
    const dataSettings = this.gridSettings.options.data;
    const obj = {
      offset: isFirstCall ? 0 : this.offset,
      limit: this.limit,
      currency: dataSettings.currency === "Base" ? "" : dataSettings.currency,
      period: dataSettings.periodData.period,
      period_type: dataSettings.periodData.period_type,
      period_basis: dataSettings.periodData.period_basis,
      viewby:
        dataSettings.viewBy === "percentage_total"
          ? "values"
          : dataSettings.viewBy,
      percentage_total: dataSettings.viewBy === "percentage_total",
      revision: dataSettings.revision,
      revenue_type: dataSettings.revenue_type,
      columns: [...this.gridSettings.columnList],
      sort_key: this.sortKey,
      sort_order: this.sortOrder,
      filter: {
        ...this.gridSettings.options.filter,
      },
      proxy: this.showNIB,
    };
    return obj;
  }
  updateTotalLineParams() {
    const dataSettings = this.gridSettings.options.data;
    const obj = {
      currency: dataSettings.currency === "Base" ? "" : dataSettings.currency,
      period: dataSettings.periodData.period,
      period_type: dataSettings.periodData.period_type,
      period_basis: dataSettings.periodData.period_basis,
      viewby:
        dataSettings.viewBy === "percentage_total"
          ? "values"
          : dataSettings.viewBy,
      revenue_type: dataSettings.revenue_type,
      columns: [...this.gridSettings.columnList],
      filter: {
        ...this.gridSettings.options.filter,
      },
      proxy: this.showNIB,
    };
    return obj;
  }
  getGridDataCall(isFirstCall) {
    this.periodList = this.gridSettings.options.data.periodData.period;
    this.displayColumns = [
      ...this.staticDisplayColumns,
      ...this.gridSettings.columnList,
      ...this.periodList,
    ];
    this.drugsGridData.columns = this.gridSettings.columnList;

    if (isFirstCall) {
      this.store.dispatch(new DeleteDpParams());
      this.selectedDrug = "";
      this.selectedCell = "";
      this.getDefaultSort();
      if (this.divScroll)
        this.divScroll.nativeElement.scroll({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
    }
    const payload: GetGridApiParams = this.updateParams(isFirstCall);

    //Handling for adding company name hover on ticker in grid
    if (!payload.columns.includes("companyname")) {
      payload.columns.push("companyname");
    }

    if (
      !isFirstCall &&
      payload.offset + payload.limit >= this.totalRows &&
      this.drugsGridData.rows.length >= this.totalRows
    ) {
      return;
    }
    if (this.dataCallSubscription) {
      this.dataCallSubscription.unsubscribe();
    }
    this.isGridReady = true;
    this.showLoader(!isFirstCall);
    this.scrollDisabled = true;
    this.dataCallSubscription = this.service.getGridData(payload).subscribe(
      (resp) => {
        this.scrollDisabled = false;
        if (
          resp.status === "success" &&
          Object.keys(resp.data).length === 0 &&
          isFirstCall
        ) {
          this.errorMsg = NoDataMsg;
          this.showError = true;
          this.isGridReady = true;
        } else if (resp.status === "success" && isFirstCall) {
          this.drugsGridData.rows = resp.sorted_ids;
          this.drugsGridData.rowData = this.formatData(resp.data);

          this.sortObj.disableClear = true;
          this.sortObj.start = "desc";
          this.sortObj.active = this.sortKey;
          this.sortObj.direction = this.sortOrder;
          this.dataSource.sort = this.sortObj;
          this.totalRows = resp.total_count;
          this.consolidatedTotal = resp.consolidated_count;
          // this.download_limit = resp.download_limit;
          this.totalCountChanged.emit(this.totalRows);
          this.consolidatedCountChanged.emit(this.consolidatedTotal);
          this.full_export_access = resp.full_export_access;

          // this.full_export_access = true;
          this.fullExportFlagChanged.emit(this.full_export_access);

          if (this.full_export_access) {
            this.download_limit = this.totalRows;
          } else {
            this.download_limit = resp.download_limit;
          }
          this.magnitudeMsg = resp.magnitude_message;
          this.offset = this.drugsGridData.rows.length;

          this.dataSource.data = this.updateDataSource(this.drugsGridData.rows);
          // saving payload for export
          const exportPayload = JSON.parse(JSON.stringify(payload));
          exportPayload.offset = 0;
          exportPayload.limit = this.download_limit;
          sessionStorage.setItem("payload", JSON.stringify(exportPayload));
          //this.table.renderRows()
          if (this.indicationBreakout) {
            this.expandAllBreakouts();
          }
        } else if (resp.status === "success") {
          this.drugsGridData.rows.push(...resp.sorted_ids);
          this.drugsGridData.rowData = {
            ...this.drugsGridData.rowData,
            ...this.formatData(resp.data),
          };
          this.hideLoader(true);

          this.dataSource.data = [
            ...this.dataSource.data,
            ...this.updateDataSource(resp.sorted_ids),
          ];
          this.offset = this.drugsGridData.rows.length;
          this.table.renderRows();
          if (this.indicationBreakout) {
            this.expandAllBreakouts();
          }
        } else {
          this.errorMsg = resp.message;
          this.showError = true;
          this.isGridReady = true;
          this.hideLoader();
        }
      },
      (err) => {
        this.errorMsg = err.message;
        this.showError = true;
        this.isGridReady = true;
        this.hideLoader();
      },
    );
    if (isFirstCall) {
      this.getTotalLineGridCall();
    }
  }

  getTotalLineGridCall() {
    const payload: GetTotalLineApiParams = this.updateTotalLineParams();
    this.isTotalLoading = true;
    this.totalLineData = {};
    if (this.totalLineCallSubscriber) {
      this.totalLineCallSubscriber.unsubscribe();
    }
    this.totalLineCallSubscriber = this.service
      .getTotalLineData(payload)
      .subscribe((resp) => {
        this.isTotalLoading = false;
        if (resp) {
          this.totalLineData = resp.data;
          for (const period in this.totalLineData.total_line_data) {
            if (
              Object.prototype.hasOwnProperty.call(
                this.totalLineData.total_line_data,
                period,
              )
            ) {
              this.totalLineData.total_line_data[period] = this.formatCellValue(
                this.totalLineData.meta_data,
                this.totalLineData.total_line_data[period],
              );
            }
          }
        }
      });
  }

  showLoader(onScroll) {
    const dummyRows: Array<GridRowObj> = [];
    for (let index = 0; index < 20; index++) {
      dummyRows.push({
        ctag: index,
        dataObj: {
          isLoading: true,
        },
        children: [],
      });
    }
    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource([]);
      setTimeout(() => {
        this.dataSource.data = onScroll
          ? [...this.dataSource.data, ...dummyRows]
          : dummyRows;
      }, 0);
    } else {
      this.dataSource.data = onScroll
        ? [...this.dataSource.data, ...dummyRows]
        : dummyRows;
    }
  }

  hideLoader(onScroll?) {
    if (onScroll) {
      this.dataSource.data = this.dataSource.data.filter((val) => {
        return val.dataObj.isLoading === false;
      });
    } else {
      this.dataSource.data = this.dataSource.data.map((val) => {
        val.dataObj.isLoading = false;
        return val;
      });
    }
  }

  handleDoubleClick(row, callFromExpandAll?, event?) {
    if (event !== undefined && event.detail === 2) {
      return;
    }
    this.expandRow(row, callFromExpandAll);
    event.stopPropagation();
  }

  handleSingleClick(row, callFromExpandAll?, event?) {
    if (event !== undefined && event.detail === 2) {
      return;
    }
    this.expandRow(row, callFromExpandAll);
    event.stopPropagation();
  }
  expandRow(row, callFromExpandAll?) {
    if (row.dataObj.rowExpanded) {
      this.collapseRow(row);
      this.table.renderRows();
      return;
    }

    if (row.children && row.children.length) {
      this.addChildren(row);
      row.dataObj["rowExpanded"] = true;
      this.dataSource.data = [...this.dataSource.data];
      setTimeout(() => this.table.renderRows(), 0);
      return;
    }
    row.dataObj["rowExpandedLoading"] = true;
    const dataSettings = this.gridSettings.options.data;
    const ColumnListForBreakout = [
      "revenue_type",
      "drug_name",
      "peak_year",
      "max_year",
      "probability_of_success",
      "peak_value",
      "max_value",
      "approval_date",
      "expiry_date",
    ];
    const sortKey =
      ColumnListForBreakout.includes(this.sortKey) ||
      dataSettings.periodData.period.includes(this.sortKey)
        ? this.sortKey
        : "";
    const order =
      ColumnListForBreakout.includes(this.sortKey) ||
      dataSettings.periodData.period.includes(this.sortKey)
        ? this.sortOrder
        : "";
    const obj = {
      currency: dataSettings.currency === "Base" ? "" : dataSettings.currency,
      period: dataSettings.periodData.period,
      period_type: dataSettings.periodData.period_type,
      period_basis: dataSettings.periodData.period_basis,
      viewby:
        dataSettings.viewBy === "percentage_total"
          ? "values"
          : dataSettings.viewBy,
      revision: dataSettings.revision,
      revenue_type: dataSettings.revenue_type,
      columns: this.gridSettings.columnList,
      companyid: row.dataObj.cid,
      sort_key: sortKey,
      sort_order: order,
      breakout_data: {
        ctagid: row.ctag,
        dapparameterids: [...row.dataObj.breakout_list],
      },
      percentage_total: dataSettings.viewBy === "percentage_total",
    };

    //get breakout data

    this.breakoutSubscriber = this.service
      .getBreakoutData(obj)
      .subscribe((resp) => {
        if (resp.status === "success") {
          this.drugsGridData.rowData = {
            ...this.drugsGridData.rowData,
            ...this.formatData(resp.data, true, resp.ctagid),
          };
          row = this.updateDataSource(resp.sorted_breakout, row);
          this.dataSource.data = [...this.dataSource.data];
          if (!callFromExpandAll) {
            this.table.renderRows();
            this.service.logUserAction("INDICATION_BREAKDOWN_ROW_ON", {
              row_data: {
                ctag_id: row.ctag,
                dapparameter_ids: [...row.dataObj.breakout_list],
              },
            });
          }
          row.dataObj["rowExpandedLoading"] = false;

          row.dataObj["rowExpanded"] = true;
        }
      });
  }
  collapseRow(row, callFromCollapseALL?) {
    row.dataObj["rowExpandedLoading"] = true;
    this.removeChildren(row);
    row.dataObj.rowExpanded = false;
    this.dataSource.data = [...this.dataSource.data];
    row.dataObj["rowExpandedLoading"] = false;
    if (!callFromCollapseALL)
      this.service.logUserAction("INDICATION_BREAKDOWN_ROW_OFF", {
        row_data: {
          ctag_id: row.ctag,
          dapparameter_ids: [...row.dataObj.breakout_list],
        },
      });
  }

  expandAllBreakouts() {
    const rows = this.dataSource.data.filter((val) => {
      return val.dataObj.breakout_list.length !== 0;
    });
    rows.forEach((row) => {
      if (!row.dataObj.rowExpanded) {
        if (row.children && row.children.length) {
          this.addChildren(row);
          row.dataObj["rowExpanded"] = true;
        } else this.expandRow(row, true);
      }
    });
    this.dataSource.data = [...this.dataSource.data];
    setTimeout(() => {
      this.table.renderRows();
    }, 10);
  }

  collapseAllBreakouts() {
    const rows = this.dataSource.data.filter((val) => {
      return val.dataObj.breakout_list?.length !== 0;
    });
    rows.forEach((element) => {
      if (element.dataObj.rowExpanded) this.collapseRow(element, true);
    });
    this.table.renderRows();
  }
  expandCell(dataObj, facet?) {
    //change icon too
    if (!dataObj.expanded) {
      dataObj["expanded"] = true;
      this.service.logUserAction("CELL_EXPANDED", {
        cell_data: {
          facet: facet,
          data: dataObj,
        },
      });
    } else {
      dataObj.expanded = false;
      this.service.logUserAction("CELL_COLLAPSED", {
        cell_data: {
          facet: facet,
          data: dataObj,
        },
      });
    }
  }
  isDate(val: any) {
    const dateCheck = new Date(val);
    return dateCheck.toString() !== "Invalid Date";
  }
  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.totalRows;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.drugsGridData.rowData.forEach((row) => this.selection.select(row));
  }
  formatData(data, isBreakout?, parentCtag?) {
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const element = data[key];
        for (const period in element.period_data) {
          if (
            Object.prototype.hasOwnProperty.call(element.period_data, period)
          ) {
            element.period_data[period] = this.formatCellValue(
              element.meta_data,
              element.period_data[period],
            );
          }
        }
        if (element.peak_value) {
          element.peak_value.value = this.formatCellValue(
            element.peak_value,
            element.peak_value.value,
          );
        }
        if (element.max_value) {
          element.max_value.value = this.formatCellValue(
            element.max_value,
            element.max_value.value,
          );
        }
        if (element.aggregate_year_5) {
          element.aggregate_year_5.value = this.formatCellValue(
            element.aggregate_year_5,
            element.aggregate_year_5.value,
          );
        }
      }
    }
    if (isBreakout) {
      for (const ctag in data) {
        //new logic

        const dataObj = data[ctag];
        const parentObj = this.drugsGridData.rowData[parentCtag];
        dataObj.breakout_list = [];
        dataObj.period_list = dataObj.period_data;
        dataObj.dapparameter = ctag;
        dataObj.isBreakout = true;
        for (const key in parentObj) {
          if (
            !BreakOutColsAllowed.includes(key) &&
            this.displayColumns.includes(key)
          ) {
            switch (key) {
              // case "peak_value":
              //   dataObj[key] = {
              //     value: null,
              //   };
              //   break;
              // case "peak_year":
              //   dataObj[key] = null;
              //   break;
              // case "probability_of_success":
              //   dataObj[key] = null;
              //   break;
              case "aggregate_year_5":
                dataObj[key] = {
                  value: null,
                };
                break;
              case "drug_name":
              case "ticker":
              case "moa":
              case "target":
              case "therapeutic_area":
              case "launch_year":
              case "approval_date":
              case "drug_type":
              case "companyname":
              case "alias_name":
              case "api":
              case "delivery_mechanism":
              case "drug_class":
              case "generic_biosimilar":
              case "originator_licensee":
              case "commercial_status":
              default:
                dataObj[key] = data[ctag][key];
            }
          } else if (
            !BreakOutColsAllowed.includes(key) &&
            !this.displayColumns.includes(key) &&
            !dataObj[key]
          ) {
            dataObj[key] = parentObj[key];
          }
        }
      }
    }
    return data;
  }

  formatCellValue(metaData, value) {
    const newVal = formatCell(
      value,
      metaData.currency,
      metaData.unit,
      "Amount",
    );
    return newVal;
  }

  updateDataSource(rows, parentObj?) {
    if (!parentObj) {
      const updatedArr = rows
        .filter((id) => this.drugsGridData.rowData[id])
        .map((id) => {
          return {
            ctag: this.drugsGridData.rowData[id]["ctagid"],
            dataObj: { ...this.drugsGridData.rowData[id], isLoading: false },
            children: null,
          };
        });

      return updatedArr;
    }
    parentObj.children = rows
      .filter((id) => this.drugsGridData.rowData[id])
      .map((id) => {
        return {
          ctag: this.drugsGridData.rowData[id]["ctagid"],
          dataObj: {
            ...this.drugsGridData.rowData[id],
            cid: parentObj.dataObj["cid"],
            isLoading: false,
          },
          children: null,
          parentCtag: parentObj.ctag,
        };
      });
    const index = this.dataSource.data.findIndex(
      (x) => x.ctag === parentObj.ctag,
    );
    this.dataSource.data.splice(index + 1, 0, ...parentObj.children);
    return parentObj;
  }

  addChildren(parentObj) {
    const index = this.dataSource.data.findIndex(
      (x) => x.ctag === parentObj.ctag,
    );
    this.dataSource.data.splice(index + 1, 0, ...parentObj.children);
  }

  removeChildren(parentObj) {
    const index = this.dataSource.data.findIndex(
      (x) => x.ctag === parentObj.ctag,
    );
    this.dataSource.data.splice(index + 1, parentObj.children.length);
  }
  loadFormulaBar(dataPointObj, period, parentCtag?) {
    this.selectedCell = "";
    this.selectedDrug = "";
    const growthType = encodeURI(
      this.gridSettings.options.data.viewBy === "values"
        ? "Analytics"
        : this.gridSettings.options.data.viewBy === "percentage_total"
          ? "% of Total"
          : "YoY%",
    );
    if (growthType !== "Analytics") {
      return;
    }
    this.selectedDrug = `${dataPointObj.drug_name}_${dataPointObj.ticker}`;
    if (dataPointObj.period_data[`${period}`] === "-") {
      this.store.dispatch(new DeleteDpParams());
      return;
    }
    this.selectedCell = `${`${dataPointObj.drug_name}_${dataPointObj.ticker}`}|${period}`;
    const dObjCopy = JSON.parse(JSON.stringify(dataPointObj));
    if (typeof dObjCopy[period] === "string") {
      dObjCopy[period] = [{ name: dObjCopy[period] }];
    }
    this.service.changeDataPoint(dObjCopy, this.selectedCell);

    const payload = {
      company_id: dataPointObj.cid,
      period: period,
      dapparameterid: dataPointObj.dapparameter,
      period_basis: this.gridSettings.options.data.periodData.period_basis,
      period_type: this.gridSettings.options.data.periodData.period_type,
      growthType: growthType,
      viewby:
        this.gridSettings.options.data.viewBy === "percentage_total"
          ? "values"
          : this.gridSettings.options.data.viewBy,
      revision: this.gridSettings.options.data.revision,
    };

    // fetch data for param call
    this.dpCallSubscriber = this.service
      .getDpCallDetails(payload)
      .subscribe((resp) => {
        const today = new Date();
        const mm = today.getMonth() + 1;
        const dd = today.getDate();
        const yyyy = today.getFullYear();
        let ticker = dataPointObj.ticker;
        if (!ticker) {
          ticker = this.drugsGridData.rowData[parentCtag].ticker;
        }
        const dpParams = {
          dp: `${resp.data.rid}|${mm}/${dd}/${yyyy}`, //"16151916582|09/02/2020",
          cut: "NMV",
          ticker: ticker,
          source: "consensus",
          is_pharma: true,
          growthType:
            this.gridSettings.options.data.viewBy === "values"
              ? "Analytics"
              : this.gridSettings.options.data.viewBy === "percentage_total"
                ? "% of Total"
                : "YoY%",
          shouldLog: true,
          revision: this.gridSettings.options.data.revision,
          period: period,
          dapparameterId: resp.data.dapparameterid,
          extras: `${resp.data.rid}|${period}`, //"16151916582|CY-2020"
        };
        if (resp.data.is_forecast === false) {
          dpParams["source"] = "va actuals";
        } else {
          dpParams["source"] = "consensus";
        }

        if (dpParams.growthType === "YoY%") {
          dpParams["historical_focus"] = "VA Actuals";
        }

        if (this.gridSettings.options.data.currency !== "Base") {
          dpParams["currency"] = this.gridSettings.options.data.currency;
        }
        this.store.dispatch(new SetDpParams(dpParams));
      });
  }
  loadFormulaBarNonPeriod(dataPointObj, colKey, ctag?, parentCtag?) {
    this.selectedCell = "";
    this.selectedDrug = "";
    let breakoutFlag = false;
    const growthType = encodeURI(
      this.gridSettings.options.data.viewBy === "values"
        ? "Analytics"
        : this.gridSettings.options.data.viewBy === "percentage_total"
          ? "% of Total"
          : "YoY%",
    );
    if (growthType !== "Analytics") {
      return;
    }
    this.selectedDrug = `${dataPointObj.drug_name}_${dataPointObj.ticker}`;
    this.selectedCell = `${`${dataPointObj.drug_name}_${dataPointObj.ticker}`}|${colKey}`;
    if (colKey === "max_value") {
      if (dataPointObj.max_value && dataPointObj.max_value["value"] === "-") {
        this.store.dispatch(new DeleteDpParams());
        return;
      }
    }
    if (colKey === "peak_sales") {
      if (dataPointObj.peak_value && dataPointObj.peak_value["value"] === "-") {
        this.store.dispatch(new DeleteDpParams());
        return;
      }
    }
    if (colKey === "pos") {
      if (
        dataPointObj.probability_of_success === null ||
        dataPointObj.probability_of_success === undefined
      ) {
        this.store.dispatch(new DeleteDpParams());
        return;
      }
    }
    if (parentCtag || dataPointObj.consolidated === false) {
      breakoutFlag = true;
    } else {
      breakoutFlag = false;
    }
    const dObjCopy = JSON.parse(JSON.stringify(dataPointObj));
    this.service.changeDataPoint(dObjCopy, this.selectedCell);

    // Create nonPeriodPayload for "pos" or "peak_value"
    this.nonPeriodPayload = {
      companyid: dataPointObj.cid,
      dapparameterid: dataPointObj.dapparameter,
      revenue_type: this.gridSettings.options.data.revenue_type,
      col: colKey,
      ctagid: parentCtag ? parentCtag : ctag,
      period_basis: this.gridSettings.options.data.periodData.period_basis,
      is_breakout: breakoutFlag,
      is_geo: this.gridSettings.options.filter.geo.length > 0,
    };
    // Call getNonPeriodDpCallDetails with nonPeriodPayload
    this.dpCallSubscriber = this.service
      .getNonPeriodDpCallDetials(this.nonPeriodPayload)
      .subscribe((resp) => {
        const today = new Date();
        const mm = today.getMonth() + 1;
        const dd = today.getDate();
        const yyyy = today.getFullYear();
        let ticker = dataPointObj.ticker;
        if (!ticker) {
          ticker = this.drugsGridData.rowData[parentCtag].ticker;
        }
        const year =
          colKey === "max_value" ? resp.data.max_year : resp.data.peak_year;
        const dpParams = {
          dp: `${resp.data.rid}|${mm}/${dd}/${yyyy}`, //"16151916582|09/02/2020",
          cut: "NMV",
          ticker: ticker,
          source: "consensus",
          growthType:
            this.gridSettings.options.data.viewBy === "values"
              ? "Analytics"
              : this.gridSettings.options.data.viewBy === "percentage_total"
                ? "% of Total"
                : "YoY%",
          shouldLog: true,
          is_pharma: true,
          revision: this.gridSettings.options.data.revision,
          period: year,
          dapparameterId: resp.data.dapparameterid,
          extras: `${resp.data.rid}|${year}`, //"16151916582|CY-2020"
        };
        if (resp.data.is_forecast === false) {
          dpParams["source"] = "va actuals";
        } else {
          dpParams["source"] = "consensus";
        }

        if (dpParams.growthType === "YoY%") {
          dpParams["historical_focus"] = "VA Actuals";
        }

        if (this.gridSettings.options.data.currency !== "Base") {
          dpParams["currency"] = this.gridSettings.options.data.currency;
        }
        if (colKey === "pos") {
          dpParams["period"] = resp.data.period;
        }
        this.store.dispatch(new SetDpParams(dpParams));
      });
  }

  ngOnDestroy() {
    if (this.dataCallSubscription) {
      this.dataCallSubscription.unsubscribe();
    }
    if (this.excelExportSubscriber) {
      this.excelExportSubscriber.unsubscribe();
    }
    if (this.breakoutSubscriber) {
      this.breakoutSubscriber.unsubscribe();
    }
    if (this.dpCallSubscriber) {
      this.dpCallSubscriber.unsubscribe();
    }
    if (this.totalLineCallSubscriber) {
      this.totalLineCallSubscriber.unsubscribe();
    }
  }
  openDrug(elem) {
    if (this.hasDrugPageAccess) {
      if (elem.parentCtag && elem.parentCtag !== "") {
        //Block for child level indication breakout
        return;
      }
      this.service.logUserAction("REDIRECT_TO_DRUG_FROM_GRID", {
        ctag: elem.ctag,
      });
      window.open(`/biopharma/drug/${elem.ctag}`, "_blank");
    } else {
      this.openModal(elem);
    }
  }
  openModal(elem) {
    if (elem.parentCtag && elem.parentCtag !== "") {
      //Block for child level indication breakout
      return;
    }
    this.showDrugModal = true;
  }
  closeModal() {
    this.showDrugModal = false;
  }

  auditSource(element, colName) {
    const elementCopy = JSON.parse(JSON.stringify(element));
    if (typeof elementCopy["dataObj"][colName] === "string") {
      elementCopy["dataObj"][colName] = [
        { name: elementCopy["dataObj"][colName] },
      ];
    }
    this.store.dispatch(new DeleteDpParams());
    this.service.changeDataPoint(
      elementCopy,
      this.selectedCell,
      this.auditTrailResponse,
    );
  }

  cellContextMenu(event: MouseEvent, element, colName): void {
    const ctag = element.parentCtag ? element.parentCtag : element.ctag;
    if (colName && element.dataObj[colName]) {
      this.selectedCell = `${`${element.dataObj.drug_name}_${element.dataObj.ticker}`}|${colName}`;
      this.selectedDrug = `${element.dataObj.drug_name}_${element.dataObj.ticker}`;
      this.showContextMenu(event, element, colName);
      this.contextMenuReady = false;

      let data = null;
      if (
        colName === "drug_type" ||
        colName === "drug_name" ||
        colName === "expiry_date" ||
        colName === "approval_date"
      ) {
        data = [{ name: element.dataObj[colName] }];
      } else {
        data = element.dataObj[colName];
      }
      this.service.getAuditTrail(ctag, colName, data).subscribe((resp) => {
        if (resp && Object.keys(resp).indexOf("source_not_available") !== -1)
          this.contextMenuReady = true;
        this.sourceAvailable = !resp["source_not_available"];
        this.auditTrailResponse = resp;
      });
      this.store.dispatch(new DeleteDpParams());
    } else {
      this.store.dispatch(new DeleteDpParams());
      return;
    }
  }

  showContextMenu(event: MouseEvent, element, colName) {
    event.preventDefault();
    this.contextMenuPosition.x = `${event.clientX}px`;
    this.contextMenuPosition.y = `${event.clientY}px`;
    this.contextMenu.menuData = {
      item: {
        colKey: "Audit Sources",
        element: element,
        colName: colName,
      },
    };
    this.contextMenu.menu.focusFirstItem("mouse");
    this.contextMenu.openMenu();
  }
}
